import { SxProps, Theme } from '@mui/material';
import COLORS from '@/resources/style/Themes';

/**
 * Shared Compass Configuration Styles
 * Durable home for all compass-configuration style tokens.
 * Components across the compass_configurations feature tree should import
 * from this file rather than from any modal-scoped styles file.
 */

// Sits above UserNavBar (zIndex: 1300) and the MUI drawer default (1200), and
// below the BaseModal force-apply sub-dialog (zIndex: 2100). See plan §"OQ-2".
export const MODAL_SHELL_Z_INDEX = 1400;

// COLOR CONSTANTS

export const UNIFIED_CONFIG_COLORS = {
  // Background colors
  backgrounds: {
    light: '#fafafaff',
    white: '#fff',
  },

  // Chip colors
  chip: {
    selectedBg: '#ddddddff',
    unselectedBg: '#e4e4e4ff',
    smartAdaptiveBg: '#e2e1e1ff',
    selectedText: '#383838ff',
    unselectedText: '#8b8b8bff',
    shadow: '#7e7e7eff',
    strikethrough: '#aaa9a9ff',
  },

  // Text colors
  text: {
    primary: '#383838ff',
    secondary: '#555',
    disabled: '#9e9e9e',
    gray: '#8b8b8bff',
  },

  // Button colors
  button: {
    primary: '#1976d2',
  },

  // Border colors
  border: {
    divider: '#eee',
  },
} as const;

// SECTION STYLES

export const sectionStyles = {
  /**
   * Container for voltage sensitivity section
   */
  voltageSection: {
    width: 'auto',
    mt: 0,
    px: 3,
    pt: 2,
    pb: 0,
    background: UNIFIED_CONFIG_COLORS.backgrounds.light,
  } as SxProps<Theme>,

  /**
   * Container for current sensitivity section
   */
  currentSection: {
    width: 'auto',
    px: 3,
    pt: 2,
    pb: 0,
    background: UNIFIED_CONFIG_COLORS.backgrounds.light,
  } as SxProps<Theme>,

  /**
   * Container for alerts section
   */
  alertsSection: {
    width: 'auto',
    mb: 2,
    px: 3,
    py: 2,
    background: UNIFIED_CONFIG_COLORS.backgrounds.light,
  } as SxProps<Theme>,
} as const;

// TYPOGRAPHY STYLES

export const typographyStyles = {
  /**
   * Main section heading (e.g., "VOLTAGE SENSITIVITY")
   */
  sectionHeading: {
    fontWeight: 500,
    color: COLORS.SAFEGUARD_BLUE,
    textTransform: 'capitalize',
    letterSpacing: 0.5,
  } as SxProps<Theme>,

  /**
   * Subsection heading (e.g., "Low Range", "Medium Range")
   */
  subsectionHeading: {
    fontWeight: 500,
    fontSize: 15,
    mb: 1,
    color: COLORS.SAFEGUARD_BLUE,
  } as SxProps<Theme>,

  /**
   * Description text
   */
  description: {
    fontSize: 13,
    mb: 2,
  } as SxProps<Theme>,

  /**
   * Alert description text
   */
  alertDescription: {
    mt: 1.5,
    fontSize: 14,
    color: UNIFIED_CONFIG_COLORS.text.secondary,
  } as SxProps<Theme>,

  /**
   * Checkbox label text
   */
  checkboxLabel: (enabled: boolean) =>
    ({
      fontWeight: 400,
      fontSize: 14,
      color: enabled
        ? COLORS.SAFEGUARD_ORANGE
        : UNIFIED_CONFIG_COLORS.text.disabled,
    }) as SxProps<Theme>,
} as const;

// CHIP STYLES

export const chipStyles = {
  /**
   * Number chip (1-11) for sensitivity selection
   */
  numberChip: (
    selected: boolean,
    disabled: boolean = false,
    readonly: boolean = false,
  ) =>
    ({
      width: 32,
      height: 32,
      borderRadius: 3,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: selected
        ? UNIFIED_CONFIG_COLORS.chip.selectedText
        : UNIFIED_CONFIG_COLORS.chip.unselectedText,
      backgroundColor: selected
        ? UNIFIED_CONFIG_COLORS.chip.selectedBg
        : UNIFIED_CONFIG_COLORS.chip.unselectedBg,
      boxShadow: `2px 2px 4px ${UNIFIED_CONFIG_COLORS.chip.shadow}`,
      border: selected
        ? `2px solid ${COLORS.SAFEGUARD_ORANGE}`
        : '2px solid transparent',
      transition: 'all 0.1s ease',
      cursor: disabled || readonly ? 'default' : 'pointer',
      opacity: disabled ? 0.4 : 1, // Only apply opacity when truly disabled, not readonly
      '&:hover':
        !disabled && !readonly
          ? {
              transform: 'scale(1.2)',
              border: selected
                ? `2px solid ${COLORS.SAFEGUARD_ORANGE}`
                : `2px solid ${UNIFIED_CONFIG_COLORS.button.primary}`,
            }
          : {},
      position: 'relative',
      overflow: 'hidden',
      '&::after': !selected
        ? {
            content: '""',
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: '140%',
            height: 2,
            backgroundColor: UNIFIED_CONFIG_COLORS.chip.strikethrough,
            opacity: 0.3,
            transform: 'translate(-50%, -50%) rotate(-45deg)',
          }
        : {},
    }) as SxProps<Theme>,

  /**
   * Smart Adaptive chip
   */
  smartAdaptiveChip: (
    selected: boolean,
    disabled: boolean = false,
    readonly: boolean = false,
  ) =>
    ({
      height: 32,
      px: 2,
      borderRadius: 3,
      fontSize: 14,
      fontWeight: 500,
      display: 'flex',
      textTransform: 'capitalize',
      alignItems: 'center',
      flexShrink: 0,
      color: selected
        ? UNIFIED_CONFIG_COLORS.chip.selectedText
        : UNIFIED_CONFIG_COLORS.chip.unselectedText,
      cursor: disabled || readonly ? 'default' : 'pointer',
      backgroundColor: UNIFIED_CONFIG_COLORS.chip.smartAdaptiveBg,
      boxShadow: `2px 2px 4px ${UNIFIED_CONFIG_COLORS.chip.shadow}`,
      border: selected
        ? `2px solid ${COLORS.SAFEGUARD_ORANGE}`
        : '2px solid transparent',
      transition: 'transform 0.2s ease, border 0.2s ease, opacity 0.2s ease',
      position: 'relative',
      overflow: 'hidden',
      opacity: disabled ? 0.4 : 1, // Only apply opacity when truly disabled, not readonly
      '&:hover':
        !disabled && !readonly
          ? {
              transform: 'scale(1.1)',
              border: selected
                ? `2px solid ${COLORS.SAFEGUARD_ORANGE}`
                : `2px solid ${UNIFIED_CONFIG_COLORS.button.primary}`,
            }
          : {},
      '&::after': !selected
        ? {
            content: '""',
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: '160%',
            height: 2,
            opacity: 0.3,
            backgroundColor: UNIFIED_CONFIG_COLORS.chip.strikethrough,
            transform: 'translate(-50%, -50%) rotate(-45deg)',
            pointerEvents: 'none',
          }
        : {},
    }) as SxProps<Theme>,

  /**
   * Alert option chip (User Defined, Enabled, Disabled)
   */
  alertOptionChip: (selected: boolean) =>
    ({
      height: 32,
      px: 2,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      whiteSpace: 'nowrap',
      borderRadius: 3,
      backgroundColor: UNIFIED_CONFIG_COLORS.chip.selectedBg,
      boxShadow: `2px 2px 4px ${UNIFIED_CONFIG_COLORS.chip.shadow}`,
      cursor: 'pointer',
      color: UNIFIED_CONFIG_COLORS.chip.selectedText,
      fontWeight: 500,
      fontSize: 14,
      position: 'relative',
      transition:
        'transform 0.2s ease, border 0.2s ease, background-color 0.2s ease',
      border: selected
        ? `2px solid ${COLORS.SAFEGUARD_ORANGE}`
        : '2px solid transparent',
      '&:hover': {
        transform: 'scale(1.1)',
        border: selected
          ? `2px solid ${COLORS.SAFEGUARD_ORANGE}`
          : `2px solid ${UNIFIED_CONFIG_COLORS.button.primary}`,
      },
    }) as SxProps<Theme>,

  /**
   * Chip container wrapper
   */
  chipContainer: (disabled: boolean = false, readonly: boolean = false) =>
    ({
      cursor: disabled || readonly ? 'default' : 'pointer',
      opacity: disabled ? 0.4 : 1, // Only apply opacity when truly disabled, not readonly
    }) as SxProps<Theme>,
} as const;

// BUTTON STYLES

export const buttonStyles = {
  /**
   * Enable All button
   */
  enableAll: {
    backgroundColor: UNIFIED_CONFIG_COLORS.button.primary,
    '&:hover': {
        backgroundColor: '#185796',
      }
  } as SxProps<Theme>,

  /**
   * Disable All button
   */
  disableAll: {
    color: UNIFIED_CONFIG_COLORS.button.primary,
    border: '2px solid',
    borderColor: UNIFIED_CONFIG_COLORS.button.primary,
  } as SxProps<Theme>,
} as const;

// CHECKBOX STYLES

export const checkboxStyles = {
  /**
   * Section enable/disable checkbox
   */
  sectionToggle: (enabled: boolean) =>
    ({
      color: enabled
        ? COLORS.SAFEGUARD_ORANGE
        : UNIFIED_CONFIG_COLORS.text.disabled,
      '&.Mui-checked': { color: COLORS.SAFEGUARD_ORANGE },
    }) as SxProps<Theme>,
} as const;

// DIVIDER STYLES

export const dividerStyles = {
  /**
   * Vertical divider between chips and smart adaptive
   */
  vertical: (disabled: boolean = false) =>
    ({
      width: 2,
      height: 20,
      alignSelf: 'center',
      backgroundColor: COLORS.SAFEGUARD_BLUE,
      borderRadius: 1,
      mx: 0.5,
      opacity: disabled ? 0.4 : 1,
    }) as SxProps<Theme>,
} as const;

// MODAL STYLES

export const modalStyles = {
  /**
   * Modal header
   */
  header: {
    backgroundColor: COLORS.SAFEGUARD_BLUE,
    color: '#fff',
    px: 3,
    py: 1.5,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  } as SxProps<Theme>,

  /**
   * Modal footer
   */
  footer: {
    borderTop: `1px solid ${UNIFIED_CONFIG_COLORS.border.divider}`,
    px: 3,
    py: 2,
    textAlign: 'right',
    backgroundColor: UNIFIED_CONFIG_COLORS.backgrounds.white,
    position: 'sticky',
    bottom: 0,
    zIndex: 10,
  } as SxProps<Theme>,

  /**
   * Cancel button in footer
   */
  cancelButton: {
    mr: 2,
  } as SxProps<Theme>,

  /**
   * Configuration name input section
   */
  nameInputSection: {
    px: 3,
    pt: 3,
    pb: 2,
    background: UNIFIED_CONFIG_COLORS.backgrounds.light,
  } as SxProps<Theme>,
} as const;
